import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { Yii } from 'libraries/interfaces/Yii';
import { NotificationType } from 'libraries/interfaces/INotification';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DeleteConfirmationDialogComponent, IDeleteValidationOutput } from './delete-confirmation-modal/delete-confirmation-dialog.component';
import { PermissionResourceService } from 'libraries/service/resources/permission/permissionResource.service';
import { forkJoin } from 'rxjs';
import { ErrorTracingService } from 'libraries/service/error-tracing/error-tracing.service';
import { faTrash, IconDefinition, faPlus } from "@fortawesome/free-solid-svg-icons";

@Component({
    selector: 'block-add-remove-button',
    template: `
        <ng-template #template>
            <td mat-cell *ngIf="showButton"
                class="button-container">
                <button class="minus-button"
                    [class.disabled]="disabled || !hasPermissionToRemoveLine"
                    (keyup.enter)="openModalConfirmation()"
                    (click)="openModalConfirmation()">
                    <fa-icon [icon]="iconTrash"></fa-icon>
                </button>
                <button class="plus-button"
                    [class.disabled]="disabled || !hasPermissionToAddLine"
                    (keyup.enter)="addBlock()"
                    (click)="addBlock()">
                    <fa-icon [icon]="iconPlus"></fa-icon>
                </button>
            </td>
        </ng-template>
    `,
    styles: [`
        :host {
            display: none;
        }
        td.button-container.mat-cell {
            display: flex;
            justify-content: center;
            flex-direction: column;
            padding: 0 !important;
            width: 40px;
            min-height: 3.75rem;
            left: 10px;
            position: sticky;
            z-index: 1;
        }
        button {
            color: #337ab7;
            padding: .25rem .75rem;
        }
        button.disabled {
            color: rgba(189, 195, 199, 0.5);
            cursor: default;
        }
        button:focus {
            outline: 1px solid #414141;
        }
        button.minus-button {
            border-bottom: 1px solid rgba(189, 195, 199, 0.3);
        }
    `]
})

export class BlockAddRemoveButtonComponent implements OnInit {

    @Input()
    public showButton: boolean = true;

    @Input()
    public disabled: boolean = false;

    @Output()
    public readonly notifyRemoveBlock: EventEmitter<null> = new EventEmitter<null>();

    @Output()
    public readonly notifyAddBlock: EventEmitter<null> = new EventEmitter<null>();

    @ViewChild('template', {read: TemplateRef, static: true})
    public readonly template: TemplateRef<{}>;

    public iconTrash: IconDefinition = faTrash;

    public iconPlus: IconDefinition = faPlus;

    public wording: { [key: string]: string } = {
        deleteConfirmationMessage: Yii.t('wui-watabi-js', "La suppression de cette ligne entrainera la perte des valeurs saisies"),
        delete: Yii.t('wui-watabi-js', "Supprimer la ligne"),
        cancel: Yii.t('wui-watabi-js', "Annuler"),
    };

    public notificationType: typeof NotificationType = NotificationType;
    public hasPermissionToAddLine: boolean = false;
    public hasPermissionToRemoveLine: boolean = false;

    constructor(
        private readonly viewContainerRef: ViewContainerRef,
        public readonly dialog: MatDialog,
        private readonly permissionResource: PermissionResourceService,
        private readonly errorTracingService: ErrorTracingService,
    ) {
    }

    public ngOnInit(): void {
        this.viewContainerRef.createEmbeddedView(this.template);

        forkJoin([
            this.permissionResource.hasPermission("quotation.cost.line.add"),
            this.permissionResource.hasPermission("quotation.cost.line.remove"),
        ]).subscribe({
            next: ([hasPermissionToAdd, hasPermissionToRemove]: [boolean, boolean]): void => {
                this.hasPermissionToAddLine = hasPermissionToAdd;
                this.hasPermissionToRemoveLine = hasPermissionToRemove;
            },
            error: (error: Error): void => {
                this.errorTracingService.captureException(error);
            }
        });
    }

    public openModalConfirmation(): void {
        if (!this.disabled) {
            let editDialog: MatDialogRef<DeleteConfirmationDialogComponent>;
            editDialog = this.dialog.open(DeleteConfirmationDialogComponent, {
                width: "50%",
                height: "fit-content",
                maxWidth: "800px",
                autoFocus: true,
            });

            editDialog.afterClosed()
                .subscribe(({ doDelete }: IDeleteValidationOutput) => {
                    if (doDelete) {
                        this.removeBlock();
                    }
                });
        }
    }

    public removeBlock(): void {
        if (!this.disabled) {
            this.notifyRemoveBlock.emit();
        }
    }

    public addBlock(): void {
        if (!this.disabled) {
            this.notifyAddBlock.emit();
        }
    }

}
